import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { TypedMemo } from '../../../common/types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { useSMLPaginationParam, defaultPaginationParamName } from '../../hooks/useSMLPaginationParam';
import { useUserTokenDecoded } from '../../../../redux/slices/appInfo/hooks';
import { SMLUpgradeModal } from '../SMLUpgradeModal/SMLUpgradeModal';
import { SMLUpgradeLockBanner } from '../SMLUpgradeLockBanner/SMLUpgradeLockBanner';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import styles from './SMLPagination.module.scss';

interface SMLPaginationProps {
  totalItems?: number;
  itemsPerPage?: number;
  showItemCount?: boolean;
  paramName?: string;
  // Used when totalItems is unknown (offset pagination): the backend's hasMore
  // flag for the current page. Ignored when totalItems is provided.
  hasMore?: boolean;
}

const SMLPaginationComponent: React.FC<SMLPaginationProps> = ({
  totalItems,
  itemsPerPage = 10,
  showItemCount = false,
  paramName = defaultPaginationParamName,
  hasMore,
}) => {
  const smlRouter = useSMLRouter();
  const { currentPage } = useSMLPaginationParam(paramName);
  const [searchParams] = useSearchParams();
  const userTokenDecoded = useUserTokenDecoded();
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);

  const totalPages = totalItems !== undefined ? Math.ceil(totalItems / itemsPerPage) : undefined;
  const canGoBack = currentPage > 1;

  // Calculate the next page's offset (0-indexed)
  const nextPageOffset = useMemo(() => {
    return currentPage * itemsPerPage;
  }, [currentPage, itemsPerPage]);

  // Check if next page would exceed user's license limit
  const wouldExceedMaxOffset = useMemo(() => {
    if (userTokenDecoded?.licenseInfo?.smlCapabilities?.maxOffset === undefined) {
      return false;
    }
    return nextPageOffset > userTokenDecoded.licenseInfo.smlCapabilities.maxOffset;
  }, [nextPageOffset, userTokenDecoded?.licenseInfo?.smlCapabilities?.maxOffset]);

  const canGoNext = useMemo(() => {
    if (wouldExceedMaxOffset) {
      return false;
    }
    if (totalPages !== undefined) {
      return currentPage < totalPages;
    }
    // No known total: gate on the backend's hasMore when provided, otherwise
    // fall back to legacy unbounded behavior (next always allowed).
    if (hasMore !== undefined) {
      return hasMore;
    }
    return true;
  }, [wouldExceedMaxOffset, totalPages, currentPage, hasMore]);

  const handlePreviousPage = useCallback(() => {
    if (canGoBack) {
      const newPage = currentPage - 1;
      const currentParams = Object.fromEntries(searchParams.entries());
      smlRouter.setParams({
        ...currentParams,
        [paramName]: newPage > 1 ? newPage.toString() : undefined,
      } as any);
    }
  }, [smlRouter, currentPage, canGoBack, searchParams, paramName]);

  const handleNextPage = useCallback(() => {
    if (canGoNext) {
      const newPage = currentPage + 1;
      const currentParams = Object.fromEntries(searchParams.entries());
      smlRouter.setParams({
        ...currentParams,
        [paramName]: newPage.toString(),
      } as any);
    }
  }, [smlRouter, currentPage, canGoNext, searchParams, paramName]);

  const showUpselling = useCallback(() => {
    setIsUpsellModalOpen(true);
  }, []);

  const handleCloseUpsellModal = useCallback(() => {
    setIsUpsellModalOpen(false);
  }, []);

  return (
    <>
      <div className={styles.paginationContainer}>
        <button
          type="button"
          className={`${styles.paginationButton} ${!canGoBack ? styles.disabled : ''}`}
          onClick={handlePreviousPage}
          disabled={!canGoBack}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>

        <div className={styles.pageInfo}>
          <span className={styles.pageNumber}>Page {currentPage}</span>
          {totalPages !== undefined && <span className={styles.totalPages}> of {totalPages}</span>}
          {showItemCount && totalItems !== undefined && <span className={styles.itemCount}>({totalItems} items)</span>}
        </div>

        <button
          type="button"
          className={`${styles.paginationButton} ${!wouldExceedMaxOffset && !canGoNext ? styles.disabled : ''}`}
          onClick={wouldExceedMaxOffset ? showUpselling : handleNextPage}
          disabled={!wouldExceedMaxOffset && !canGoNext}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>

      {wouldExceedMaxOffset && (
        // The pages the plan does cover stay on screen. Only the step past the limit is sold.
        <SMLUpgradeLockBanner
          feature={SMLUpgradeFeature.browseDepth}
          onSeePlan={showUpselling}
          className={styles.lockBanner}
        />
      )}

      <SMLUpgradeModal
        isOpen={isUpsellModalOpen}
        onClose={handleCloseUpsellModal}
        feature={SMLUpgradeFeature.browseDepth}
      />
    </>
  );
};

export const SMLPagination = TypedMemo(SMLPaginationComponent);
